<template>
  <div class="table-wrapper">
    <table class="table">
      <thead>
        <tr>
          <th
            v-for="(column, index) in isHideRevenue
              ? columns.filter(
                  c => !['予算', '消化金額', 'CPC', 'CPM'].includes(c.title),
                )
              : columns"
            :key="index"
            :class="[
              'head__column',
              {
                active:
                  insertionOrderReportSortable.sortBy === column.sort.value,
              },
              { pointer: true === column.sort.able },
            ]"
            @click="insertionOrderReportSortable.changeSort(column.sort.value)"
            :style="{ textAlign: column.textAlign }"
          >
            <v-tooltip v-if="column.title === '再生完了数'" top>
              <template v-slot:activator="{ on }">
                <v-icon v-on="on" small color="primary">info</v-icon>
              </template>
              <span>
                対象の案件で、動画、オーディオクリエイティブを配信した場合は、<br />再生完了数がカウントされます。
              </span>
            </v-tooltip>
            {{ column.title }}
            <v-icon
              v-if="column.sort.able"
              :class="{
                reverse:
                  insertionOrderReportSortable.sortBy === column.sort.value &&
                  !insertionOrderReportSortable.sortDirection,
              }"
              :small="true"
            >
              arrow_downward
            </v-icon>
          </th>
        </tr>
      </thead>
      <tbody>
        <template v-for="item in filteredItems">
          <IoRow
            :key="`io-${item.id}`"
            :data="item"
            :fetched-color="fetchedColor"
            :open-campaign="isCampaignOpen(item)"
            :selected-billing-period-ids="selectedBillingPeriodIds"
            :is-hide-revenue="isHideRevenue"
            @toggleCampaign="toggleCampaign(item)"
          />
          <CampaignTable
            v-if="isCampaignOpen(item)"
            :onClickDeliveryGroupExpandButton="onClickDeliveryGroupExpandButton"
            :onClickCampaignBudgetSelector="onClickCampaignBudgetSelector"
            :key="`campaign-${item.id}`"
            :campaignReportSortable="campaignReportData(item.id)"
            :deliveryGroupReportSortables="deliveryGroupReportSortables"
            :openCampaignIds="openCampaignIds"
            :fetched-color="fetchedColor"
            :selected-billing-period-ids="selectedBillingPeriodIds"
            :isHideRevenue="isHideRevenue"
          />
        </template>
      </tbody>
    </table>
    <CampaignRowBudgetSelector
      :insertion-order-id="selectedInsertionOrderId"
      :campaign-budgets="campaignBudgets"
      :selected-campaign-budget-ids="selectedCampaignBudgetIds"
      :campaign-id="selectedCampaignId"
      @close="onChangeCampaignBudgetIds"
      @cancel="onCancel"
      :isOpen="isOpenCampaignBudgetSelector"
    />
  </div>
</template>

<script>
import difference from 'lodash/difference'
import {
  insertionOrderColumns as columns,
  defaultSortDirection,
} from './tableColumns'
import IoRow from './MainTableRowIO'
import CampaignRowBudgetSelector from './MainTableCampaignRowBudgetSelector'
import CampaignTable from './MainTableCampaignRows'
import { mapActions, mapGetters } from 'vuex'
import { Sortable } from '@/models/StandardReport'

export default {
  name: 'MainTable',
  components: { IoRow, CampaignTable, CampaignRowBudgetSelector },
  props: {
    reportData: {
      type: Array,
      default: () => [],
    },
    range: {
      type: Object,
      default: () => {},
    },
    selectedInsertionOrderIds: {
      type: Array,
      default: null,
    },
    selectedBillingPeriodIds: {
      type: Array,
      default: null,
    },
    isHideRevenue: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      sortBy: 'none',
      sortDirectionObject: { ...defaultSortDirection },
      openInsertionOrderIds: [],
      insertionOrderReportSortable: new Sortable({ items: this.reportData }),
      // キャンペーンレポート表示に関するdata
      openCampaignIds: [],
      campaignReportSortables: {},
      isOpenCampaignBudgetSelector: false,
      campaignBudgets: [],
      selectedInsertionOrderId: 0,
      selectedCampaignId: 0,
      selectedCampaignBudgetIds: [],
      // 配信グループレポート表示に関するdata
      deliveryGroupReportSortables: {},
    }
  },
  computed: {
    ...mapGetters('advertiser/standardReport', [
      'getCampaignReportData',
      'getDeliveryGroupReportData',
      'getSelectedCampaignBudgetIds',
    ]),
    sortDirection() {
      return this.sortDirectionObject[this.sortBy]
    },
    items() {
      return this.insertionOrderReportSortable.sortedItems
    },
    filteredItems() {
      return this.items.filter(
        item =>
          this.selectedInsertionOrderIds == null ||
          this.selectedInsertionOrderIds.includes(item.id),
      )
    },
    fetchedColor() {
      return this.$store.getters['colorTheme/color']
    },
    columns() {
      return columns
    },
  },
  watch: {
    reportData: {
      handler() {
        this.insertionOrderReportSortable.items = this.reportData
      },
      deep: true,
    },
    range() {
      this.closeAllInsertionOrders()
      this.closeAllCampaigns()
    },
    async selectedInsertionOrderIds() {
      this.closeAllInsertionOrders()
      this.closeAllCampaigns()
      await this.fetchCampaignReportData()
    },
    async selectedBillingPeriodIds() {
      this.closeAllCampaigns()
      await this.fetchCampaignReportData()
    },
  },
  methods: {
    ...mapActions('advertiser/standardReport', {
      fetchCampaignReport: 'FETCH_CAMPAIGN_REPORT',
      fetchDeliveryGroupReport: 'FETCH_DELIVERY_GROUP_REPORT',
      setSelectedCampaignBudgetIds: 'SET_SELECTED_CAMPAIGN_BUDGET_IDS',
      fetchCampaignBudget: 'FETCH_CAMPAIGN_BUDGET',
    }),
    ...mapActions('loader', {
      showLoader: 'SHOW',
      hideLoader: 'HIDE',
    }),
    /**
     * キャンペーンレポートのロジック
     */
    // budgetIdsでフィルタする
    async onClickCampaignBudgetSelector(campaignId, insertionOrderId) {
      this.selectedInsertionOrderId = insertionOrderId
      this.selectedCampaignId = campaignId
      this.campaignBudgets = this.campaignReportSortables[
        this.campaignKey(insertionOrderId)
      ].sortedItems.find(item => item.id === campaignId).budgets
      this.selectedCampaignBudgetIds = this.getSelectedCampaignBudgetIds(
        campaignId,
      )
      // modal open
      this.isOpenCampaignBudgetSelector = true
    },
    // セレクタで変更があったときに呼ばれる
    async onChangeCampaignBudgetIds({
      insertionOrderId,
      campaignId,
      campaignBudgetIds,
    }) {
      this.setSelectedCampaignBudgetIds({ campaignId, campaignBudgetIds })
      await this.setCampaignReport(
        insertionOrderId,
        campaignBudgetIds,
        campaignId,
      )
      await this.setDeliveryGroup(campaignId, campaignBudgetIds)
      this.isOpenCampaignBudgetSelector = false
    },
    onCancel() {
      this.isOpenCampaignBudgetSelector = false
    },
    async toggleCampaign(data) {
      const insertionOrderId = data.id
      const key = this.campaignKey(insertionOrderId)
      if (this.isCampaignOpen(data)) {
        this.openInsertionOrderIds = difference(this.openInsertionOrderIds, [
          insertionOrderId,
        ])
        this.insertionOrderReportSortable[key] = undefined
      } else {
        await this.setCampaignReport(insertionOrderId)
        this.setSelectedAllCampaignBudgets(key)
        await this.setAllDeliveryGroup()
      }
    },
    async setCampaignReport(insertionOrderId, campaignBudgetIds, campaignId) {
      this.showLoader()
      const key = this.campaignKey(insertionOrderId)
      await this.fetchCampaignReport({
        insertionOrderId: insertionOrderId,
        insertionOrderBudgetIds: this.selectedBillingPeriodIds,
        campaignBudgetIds: campaignBudgetIds,
        campaignId: campaignId,
      })
      const reportData = this.getCampaignReportData(insertionOrderId)
      this.campaignReportSortables = {
        ...this.campaignReportSortables,
        [key]: new Sortable({ items: reportData }),
      }
      if (!this.openInsertionOrderIds.includes(insertionOrderId)) {
        this.openInsertionOrderIds = [
          ...this.openInsertionOrderIds,
          insertionOrderId,
        ]
      }
      this.hideLoader()
    },
    setSelectedAllCampaignBudgets(key) {
      const campaignBudgets = this.campaignReportSortables[key].items.map(
        item => {
          return {
            campaignId: item.id,
            campaignBudgetIds: item.budgets.map(
              budget => budget.campaignBudgetId,
            ),
          }
        },
      )
      campaignBudgets.forEach(budget => {
        this.setSelectedCampaignBudgetIds({
          campaignId: budget.campaignId,
          campaignBudgetIds: budget.campaignBudgetIds,
        })
      })
    },
    async setAllDeliveryGroup() {
      this.openCampaignIds.forEach(async campaignId => {
        await this.setDeliveryGroup(
          campaignId,
          this.getSelectedCampaignBudgetIds(campaignId),
        )
      })
    },
    isCampaignOpen(data) {
      return this.openInsertionOrderIds.includes(data.id)
    },
    campaignReportData(insertionOrderId) {
      return this.campaignReportSortables[this.campaignKey(insertionOrderId)]
    },
    campaignKey(insertionOrderId) {
      return `campaign-${insertionOrderId}`
    },
    /**
     * 配信グループレポートのロジック
     */
    async onClickDeliveryGroupExpandButton(campaignId) {
      // 1. 配信グループレポートを取得
      const selectedCampaignBudgetIds = this.getSelectedCampaignBudgetIds(
        campaignId,
      )
      await this.setDeliveryGroup(campaignId, selectedCampaignBudgetIds)
      // 2. 閉じる・開くの制御
      if (this.openCampaignIds.includes(campaignId)) {
        this.openCampaignIds = this.openCampaignIds.filter(element => {
          return element !== campaignId
        })
      } else {
        this.openCampaignIds.push(campaignId)
      }
    },
    async setDeliveryGroup(campaignId, campaignBudgetIds) {
      this.showLoader()
      await this.fetchDeliveryGroupReport({ campaignId, campaignBudgetIds })
      const reportData = this.getDeliveryGroupReportData(campaignId)
      const key = this.deliveryGroupKey(campaignId)
      this.deliveryGroupReportSortables = {
        ...this.deliveryGroupReportSortables,
        [key]: new Sortable({ items: reportData }),
      }
      this.hideLoader()
    },
    deliveryGroupKey(campaignId) {
      return `delivery-${campaignId}`
    },
    async fetchCampaignReportData() {
      this.openInsertionOrderIds.forEach(async insertionOrderId => {
        await this.setCampaignReport(insertionOrderId)
      })

      if (this.openInsertionOrderIds.length !== 0) {
        await this.setAllDeliveryGroup()
      }
    },
    closeAllInsertionOrders() {
      this.openInsertionOrderIds = []
    },
    closeAllCampaigns() {
      this.openCampaignIds = []
    },
  },
}
</script>

<style scoped lang="scss">
.table-wrapper {
  overflow-y: scroll;
  height: 300px;
}

.table {
  border-collapse: collapse;
  border-style: hidden;
  width: 100%;
}

.head {
  &__column {
    padding: 5px 10px;
    font-weight: normal;
    background: #ffffff;
    white-space: nowrap;
    line-height: 2;
    position: sticky;
    top: 0;
    z-index: 1;
  }
}

.campaign {
  &__header {
    text-align: right;
    padding-right: 5px;
  }

  &__column {
    text-align: right;
    padding: 5px 10px;
    border-top-width: 1px;
    border-top-style: solid;
    border-top-color: #ffffff;
    font-weight: normal;
    white-space: nowrap;

    &--bp-date {
      font-size: 10px;
    }
  }
}

.pointer {
  cursor: pointer;
}

.active {
  text-decoration: underline;
  text-decoration-color: gray;
}

.reverse {
  transform: scale(1, -1);
}

.none {
  display: none;
}

.campaign-sort {
  font-weight: normal;
  margin-right: 10px;
}
</style>
